// =========================================================================
// AlertsScene — one page per open alert. Fork-owned (zone C).
//
// Port of `alertPage(a)`, `renderAlerts(id)` and `approve(aid, i, kind)` at
// lines 2279–2293 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`, plus the
// `#v-alerts` section header at line 1445.
//
// The page answers one question in four moves: what the model thinks
// (confidence, window), why it thinks it (the reasoning paragraph), what it
// read to get there (evidence, every row opening its own source), and what it
// has drafted for a person to approve (the agent chain).
//
// The picker is a left rail of all seven alerts; the selected one is the route
// segment, so every alert is a deep link.
//
// The demo's condition panel leads with `turnStill(...)`, the machine still
// with the alerting component highlighted (line 2283). Here that is
// `shared/viz/IssueMachinePicture` (L7's `TurntableStill` inside) at the panel
// size, above the condition figure, the band and the register link.
//
// UI layer. Figures come from `alertPageData()` / `windowPct()` in the engine;
// selection, emphasis and evidence routing come from the sibling transform.
//
// @see docs/logic/issues/alerts.md
// =========================================================================

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  HelpAnchor,
  Kicker,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { cn } from '@/shared/cn';
import {
  alertPageData,
  alertPickerEntries,
  windowPct,
} from '@/versions/v1/shared/oem-engine';
import {
  ALERT_APPROVAL_TOASTS,
  type AlertChainStep,
} from '@/versions/v1/shared/seed/alerts';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import { IssueMachinePicture } from '@/versions/v1/shared/viz/IssueMachinePicture';
import { RingGauge } from '@/versions/v1/shared/viz/RingGauge';
import {
  alertsCountLabel,
  approvalKey,
  APPROVED_LABEL,
  emphasisSegments,
  evidenceTarget,
  resolveAlert,
} from './AlertsScene.transform';
import { useSourceDrawer } from './useSourceDrawer';
import { AlertRoutingPanel } from './AlertRoutingPanel';
import { CausePill, useAlertCauses } from './RouteCards';

/** One sentence (simplification pass B4). */
const DESCRIPTION =
  'Raised by the Diagnostic Agent from the monitoring feeds — every downstream action stays a draft until a planner approves it.';

export function AlertsScene() {
  const { alertId } = useParams<{ alertId: string }>();
  const alert = useMemo(() => resolveAlert(alertId), [alertId]);
  const page = useMemo(() => alertPageData(alert), [alert]);
  const win = useMemo(() => windowPct(alert), [alert]);
  const picker = useMemo(() => alertPickerEntries(), []);
  // fork(oem/v1.8): operator / equipment / mixed pill on each list card.
  const causes = useAlertCauses();

  const { show } = useToast();
  const { openDataset, pending } = useSourceDrawer();

  // Approvals are demo-local: the set of `alertId::stepIndex` a person has
  // pressed this session. Nothing is persisted — the seed stays the source of
  // truth and a reload puts the chain back as the agents left it.
  const [approved, setApproved] = useState<ReadonlySet<string>>(() => new Set());

  const approve = (step: AlertChainStep, index: number) => {
    const key = approvalKey(alert.id, index);
    setApproved((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    show(step.approvalKind ? ALERT_APPROVAL_TOASTS[step.approvalKind] : 'Approved');
  };

  const critical = alert.sev === 'crit';

  /**
   * C1. `/issues/alerts/:alertId` lands *on* the alert: the H1 is the alert's
   * own title and the seven-card picker collapses to a chip row under the
   * article. `/issues/alerts` with no id is still the list, because that is
   * what the nav row means.
   *
   * `resolveAlert` still falls back for an unknown id, so a stale deep link
   * lands on a real alert rather than an empty page — it just does not get
   * the list page by accident.
   */
  const onList = !alertId;
  const others = useMemo(
    () => picker.filter((entry) => entry.alert.id !== alert.id),
    [picker, alert.id],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="AlertsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-4" as="div">
            <Commentable name="AlertsHeader">
              <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="flex flex-wrap items-center gap-x-3 gap-y-2 text-2xl font-semibold tracking-tight text-[var(--ink-primary)]">
                    {onList ? 'Alerts' : alert.title}
                    {onList ? null : (
                      <span
                        className="rounded-full px-2.5 py-1 text-xs font-medium"
                        style={{
                          background: critical ? 'var(--vpoor-soft)' : 'var(--poor-soft)',
                          color: critical ? 'var(--vpoor)' : 'var(--poor)',
                        }}
                      >
                        {critical ? 'Critical alert' : 'Watch'}
                      </span>
                    )}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--ink-secondary)]">
                    {onList
                      ? DESCRIPTION
                      : `${alert.id} · ${alert.machine} ${page.machine.model} · ${page.site} · raised ${alert.raised}`}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <span className="mono rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-xs text-[var(--ink-secondary)]">
                    {alertsCountLabel()}
                  </span>
                  <HelpAnchor doc="issues/alerts" />
                </div>
              </header>
            </Commentable>

            <AskStrip view="alerts" />

            {/* C1: the seven-card grid is the list page, not the drill-down. */}
            {onList ? (
            <Commentable name="AlertPicker">
              <nav
                aria-label="Open alerts"
                className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-4"
              >
                {picker.map((entry) => {
                  const on = entry.alert.id === alert.id;
                  return (
                    <Link
                      key={entry.alert.id}
                      to={`/issues/alerts/${entry.alert.id}`}
                      aria-current={on ? 'page' : undefined}
                      className={cn(
                        'grid content-start gap-1 rounded-xl border p-4 text-left transition-colors',
                        on
                          ? 'border-[var(--accent)] bg-[var(--surface-1)]'
                          : 'border-[var(--hairline)] bg-[var(--surface-1)] hover:border-[var(--accent)]',
                      )}
                    >
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span
                          className="mono rounded px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-[0.04em]"
                          style={{
                            background:
                              entry.alert.sev === 'crit' ? 'var(--crit-soft)' : 'var(--warn-soft)',
                            color: entry.alert.sev === 'crit' ? 'var(--crit)' : 'var(--warn)',
                          }}
                        >
                          {entry.tag}
                        </span>
                        {causes?.get(entry.alert.id) ? (
                          <CausePill
                            cause={causes.get(entry.alert.id)!.cause}
                            confidence={causes.get(entry.alert.id)!.confidence}
                            size="xs"
                          />
                        ) : null}
                      </span>
                      <b className="text-[13px] font-semibold leading-snug text-[var(--ink-primary)]">
                        {entry.alert.title}
                      </b>
                      <small className="text-[11.5px] text-[var(--ink-tertiary)]">
                        {entry.alert.raised} · {entry.site} · {entry.alert.machine} (
                        {entry.machine.model})
                      </small>
                      <span className="mt-0.5 text-[11px] text-[var(--accent)]">
                        {entry.alert.comp} · {entry.alert.window}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            </Commentable>
            ) : null}

            {onList ? null : (
            <Commentable name="AlertPage">
              <article
                id={`al-${alert.id}`}
                className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4"
              >
                {/*
                  head — C1 moved the severity chip, the title, the id, the
                  site and the raised time up into the H1 and its meta line,
                  so what is left here is the feed mode and the way out to
                  the machine.
                */}
                <div className="mb-3 flex flex-wrap items-center gap-2.5">
                  {alert.mode === 'LIVE' ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--crit)]">
                      <i
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--crit)]"
                      />
                      Live
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--ink-tertiary)]">
                      Shift-end
                    </span>
                  )}
                  <span className="ml-auto flex flex-wrap items-center gap-2.5 text-[11px]">
                    <Link
                      to={`/machines/${alert.machine}`}
                      className="mono font-medium text-[var(--accent)] hover:underline"
                    >
                      Open {alert.machine} · {page.machine.model}
                    </Link>
                  </span>
                </div>

                {/* hero */}
                <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
                  <div className="min-w-0">
                    <div className="mb-3 flex flex-wrap items-center gap-6">
                      <div className="min-w-0">
                        <Kicker className="block">Window</Kicker>
                        <div className="mono mt-1 text-[22px] font-semibold leading-tight text-[var(--ink-primary)]">
                          {alert.window}
                        </div>
                        <div className="mt-2 max-w-sm">
                          <div className="h-[7px] overflow-hidden rounded bg-[var(--hairline)]">
                            <i
                              className={cn('block h-full', win.urgent ? 'animate-pulse' : '')}
                              style={{
                                width: `${win.pct}%`,
                                background: win.urgent
                                  ? 'var(--vpoor)'
                                  : 'linear-gradient(90deg, var(--warn), var(--crit))',
                              }}
                            />
                          </div>
                          <div className="mt-1 flex flex-wrap justify-between gap-2 text-[11px]">
                            <b className="font-semibold text-[var(--crit)]">{win.label}</b>
                            <span className="text-[var(--ink-tertiary)]">{win.fix ?? ''}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <RingGauge
                          percent={page.confidencePct}
                          tone={page.confidencePct >= 80 ? 'done' : 'now'}
                          label="conf"
                          ariaLabel={`Model confidence ${page.confidencePct} per cent`}
                          className="h-[86px] w-[86px] text-[var(--ink-primary)]"
                        />
                        <span className="text-[11.5px] leading-snug text-[var(--ink-tertiary)]">
                          model
                          <br />
                          confidence
                        </span>
                      </div>
                    </div>

                    <p className="mb-3 max-w-[78ch] text-[12.5px] leading-relaxed text-[var(--ink-secondary)]">
                      {alert.why}
                    </p>

                    <Kicker className="mb-1.5 block">Evidence pulled in</Kicker>
                    <Commentable name="AlertEvidence">
                      <div className="scrollbar-subtle overflow-x-auto rounded-lg border border-[var(--hairline)]">
                        <table className="w-full min-w-[34rem] border-collapse text-[11.5px]">
                          <thead>
                            <tr className="border-b border-[var(--hairline)] bg-[var(--surface-2)] text-left text-[10.5px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                              <th className="px-2.5 py-2 font-medium">Source</th>
                              <th className="px-2.5 py-2 font-medium">What it says</th>
                              <th className="px-2.5 py-2 font-medium">Reference</th>
                              <th className="px-2.5 py-2 text-right font-medium">Open</th>
                            </tr>
                          </thead>
                          <tbody>
                            {alert.evidence.map((item) => {
                              const target = evidenceTarget(item.ref, alert.machine);
                              return (
                                <tr
                                  key={`${item.source}::${item.ref.raw}`}
                                  className="border-b border-[var(--hairline)] last:border-0"
                                >
                                  <td className="px-2.5 py-2 font-semibold text-[var(--ink-primary)]">
                                    {item.source}
                                  </td>
                                  <td className="px-2.5 py-2 text-[var(--ink-secondary)]">
                                    {item.detail}
                                  </td>
                                  <td className="mono px-2.5 py-2 text-[10.5px] text-[var(--ink-tertiary)]">
                                    {target.raw}
                                  </td>
                                  <td className="px-2.5 py-2 text-right">
                                    {target.kind === 'dataset' ? (
                                      <button
                                        type="button"
                                        aria-busy={pending === target.dataset}
                                        onClick={() =>
                                          openDataset(target.dataset, {
                                            filter: { machine_id: alert.machine },
                                          })
                                        }
                                        className="whitespace-nowrap font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
                                      >
                                        rows ›
                                      </button>
                                    ) : (
                                      <Link
                                        to={target.to}
                                        className="whitespace-nowrap font-medium text-[var(--accent)] hover:underline"
                                      >
                                        open ›
                                      </Link>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Commentable>
                  </div>

                  {/* condition panel */}
                  <aside className="min-w-0 self-start rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] p-3.5">
                    <Link
                      to={`/machines/${alert.machine}`}
                      title={`Open ${alert.machine} — equipment details`}
                      className="mb-3 block rounded-lg border border-[var(--hairline)] transition-colors hover:border-[var(--accent)]"
                    >
                      <IssueMachinePicture machineId={alert.machine} highlight={alert.comp} size="panel" />
                    </Link>
                    <Kicker className="block">Component condition</Kicker>
                    <div className="mt-2 text-sm font-medium text-[var(--ink-primary)]">
                      {alert.comp}
                    </div>
                    <div className="mono mt-2 text-[28px] font-semibold leading-none">
                      <span style={{ color: page.band.colorToken }}>{page.score}</span>
                      <span className="text-base font-normal text-[var(--ink-tertiary)]">/100</span>
                    </div>
                    <div className="mt-2">
                      <span className={`band-pill band-${page.band.key}`}>{page.band.label}</span>
                    </div>
                    <p className="mt-3 border-t border-[var(--hairline)] pt-3 text-[11px] text-[var(--ink-tertiary)]">
                      Scored by the condition register against the component&rsquo;s change
                      interval.
                    </p>
                    <button
                      type="button"
                      aria-busy={pending === 'health'}
                      onClick={() =>
                        openDataset('health', {
                          title: 'Component condition register',
                          filter: { machine_id: alert.machine },
                        })
                      }
                      className="mt-1.5 text-[11px] font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
                    >
                      condition register ›
                    </button>
                    <Link
                      to={`/machines/${alert.machine}`}
                      className="mt-3 block rounded-md border border-[var(--hairline)] px-2.5 py-1.5 text-center text-[11px] font-medium text-[var(--ink-secondary)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    >
                      Open {alert.machine} ›
                    </Link>
                  </aside>
                </div>

                {/* agent chain */}
                <Kicker className="mb-1.5 mt-4 block">Agent chain</Kicker>
                <Commentable name="AgentChain">
                  <ol className="grid overflow-hidden rounded-lg border border-[var(--hairline)] md:grid-cols-2 2xl:grid-cols-5">
                    {alert.chain.map((step, index) => {
                      const done =
                        step.status === 'done' || approved.has(approvalKey(alert.id, index));
                      return (
                        <li
                          key={approvalKey(alert.id, index)}
                          className="flex min-w-0 flex-col gap-1.5 border-b border-r border-[var(--hairline)] p-3 last:border-r-0"
                        >
                          <div className="flex items-center gap-2 text-xs font-semibold text-[var(--ink-primary)]">
                            <i
                              aria-hidden
                              className="inline-block h-2 w-2 flex-none rounded-sm"
                              style={{ background: done ? 'var(--good)' : 'var(--accent)' }}
                            />
                            {step.agent}
                          </div>
                          <p className="text-xs leading-relaxed text-[var(--ink-secondary)]">
                            {emphasisSegments(step.output, step.emphasis).map((segment, si) =>
                              segment.bold ? (
                                <b
                                  key={si}
                                  className="mono font-medium text-[var(--ink-primary)]"
                                >
                                  {segment.text}
                                </b>
                              ) : (
                                <span key={si}>{segment.text}</span>
                              ),
                            )}
                          </p>
                          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                            <span
                              className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                              style={
                                done
                                  ? { background: 'var(--good-soft)', color: 'var(--good)' }
                                  : { background: 'var(--poor-soft)', color: 'var(--poor)' }
                              }
                            >
                              {approved.has(approvalKey(alert.id, index))
                                ? APPROVED_LABEL
                                : step.label}
                            </span>
                            {step.status === 'approve' &&
                            !approved.has(approvalKey(alert.id, index)) ? (
                              <button
                                type="button"
                                onClick={() => approve(step, index)}
                                className="rounded-md bg-[var(--accent)] px-2 py-1 text-[11px] font-medium text-[var(--accent-text)] transition-opacity hover:opacity-90"
                              >
                                Approve
                              </button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </Commentable>
              </article>
            </Commentable>
            )}

            {/* fork(oem/v1.8): root cause and routing — operator or equipment, and who acts. */}
            {onList ? null : <AlertRoutingPanel alert={alert} />}

            {/*
              C1. The other six alerts, as a chip row under the article rather
              than a seven-card grid above it. Every alert stays one click
              away and every deep link still works; the page just stops
              opening on a list when it was asked for an alert.
            */}
            {onList ? null : (
              <Commentable name="AlertPicker">
                <nav aria-label="Other open alerts" className="flex flex-wrap items-center gap-2">
                  <span className="text-[11.5px] font-medium text-[var(--ink-tertiary)]">
                    {others.length} more alert{others.length === 1 ? '' : 's'}
                  </span>
                  {others.map((entry) => (
                    <Link
                      key={entry.alert.id}
                      to={`/issues/alerts/${entry.alert.id}`}
                      title={entry.alert.title}
                      className="inline-flex max-w-[22rem] items-center gap-2 rounded-full border border-[var(--hairline)] bg-[var(--surface-1)] px-3 py-1.5 text-[11.5px] transition-colors hover:border-[var(--accent)]"
                    >
                      <i
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{
                          background:
                            entry.alert.sev === 'crit' ? 'var(--crit)' : 'var(--warn)',
                        }}
                      />
                      <span className="mono shrink-0 font-medium text-[var(--ink-primary)]">
                        {entry.alert.machine}
                      </span>
                      <span className="truncate text-[var(--ink-secondary)]">
                        {entry.alert.title}
                      </span>
                    </Link>
                  ))}
                </nav>
              </Commentable>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
